import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Card,
  Divider,
  Stack,
  SxProps,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip
} from "@mui/material";
import { ErrorPanel, formatDateTime, useToast } from "@qubit/autoparts";
import { useEffect } from "react";

import { useAppSelector } from "~/app/store";
import { selectFetchStatusEndpointsInterval } from "~/features/andon/andon.slice";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { useGetDevicesHealthQuery } from "~/redux/public/deviceHealth";
import {
  useGetAutostoreGridQuery,
  useGetAutostoreGridsQuery,
  useGetAutostoreGridStatusQuery,
  useGetAutostoreTasksQuery,
  useGetLogPublisherStateQuery
} from "~/redux/warehouse/autostoreGrid.hooks";

import { PTLHealthCard } from "./PTLHealthCard";

const copyToClipboard = (text: string) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => {
      // do nothing
    });
  }
};

const ShortenedIdDisplay = ({ id, label }: { id: string; label: string }) => {
  const shortenedId =
    id.length > 12 ? `${id.slice(0, 8)}...${id.slice(-4)}` : id;

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2">{shortenedId}</Typography>
        <Tooltip title={id}>
          <IconButton
            aria-label={`Copy ${label}`}
            size="small"
            onClick={() => copyToClipboard(id)}
            sx={{ p: 0.25 }}
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const GridCardSx: SxProps = {
  p: 2,
  mb: 2,
  minWidth: 300,
  maxWidth: 400,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  boxShadow: 1
};

const GridInfo = ({ autostoreGridId }: { autostoreGridId: Guid }) => {
  const fetchStatusEndpointsInterval = useAppSelector(
    selectFetchStatusEndpointsInterval
  );

  const { data: autostoreGrid } = useGetAutostoreGridQuery(autostoreGridId);
  const {
    data: systemStatus,
    refetch: refetchGridStatus,
    error: gridStatusError
  } = useGetAutostoreGridStatusQuery(autostoreGridId, {
    pollingInterval:
      fetchStatusEndpointsInterval > 0 ? fetchStatusEndpointsInterval : 5000
  });

  const {
    data: logPubData,
    isUninitialized: isLogPubUninitialized,
    isFetching: logPubLoading,
    refetch: refetchLogPub,
    error: logPubError
  } = useGetLogPublisherStateQuery(autostoreGridId, {
    pollingInterval:
      fetchStatusEndpointsInterval > 0 ? fetchStatusEndpointsInterval : 60000
  });
  const {
    data: autostoreTasks,
    isUninitialized: isAutostoreTasksUninitialized,
    isFetching: autostoreTasksLoading,
    refetch: refetchAutostoreTasks,
    error: autostoreTasksError
  } = useGetAutostoreTasksQuery(autostoreGridId, {
    pollingInterval:
      fetchStatusEndpointsInterval > 0 ? fetchStatusEndpointsInterval : 60000
  });

  const refetchAll = async () => {
    if (!isLogPubUninitialized) await refetchLogPub();
    if (!isAutostoreTasksUninitialized) await refetchAutostoreTasks();
  };

  // Determine connection status
  const isConnected = systemStatus?.kind === "connected";
  const connectionText = isConnected ? "Connected" : "Disconnected";
  const pickingTaskGroupCount =
    autostoreTasks?.filter((t) => t.category === 1) ?? [];
  const totalPickingTasks = pickingTaskGroupCount.reduce(
    (acc, curr) => acc + curr.tasks.length,
    0
  );
  const totalTasks =
    autostoreTasks?.reduce((acc, curr) => acc + curr.tasks.length, 0) ?? 0;
  const preparedBins = Object.values(logPubData?.binsByPos ?? {})
    .flatMap((b) => b)
    .filter((b) =>
      ["Prepared", "Closed", "Open", "Port", "To Grid", "Unknown"].includes(
        b.binMode
      )
    );
  const preparedTasks =
    autostoreTasks
      ?.flatMap((t) => t.tasks)
      .filter((t) => preparedBins.some((b) => b.binId === t.binId)) ?? [];

  if (!autostoreGrid) return <></>;

  return (
    <>
      <Card sx={GridCardSx}>
        {/* Header with Grid Name and Connection Status */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" component="div">
            {autostoreGrid?.autostoreGridName}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: isConnected ? "success.main" : "error.main"
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {connectionText}
            </Typography>
          </Box>
        </Box>

        {/* Grid Details */}
        <Box display="flex" flexDirection="column" gap={1}>
          <ShortenedIdDisplay id={autostoreGridId} label="Grid ID" />
          {gridStatusError && (
            <ErrorPanel
              message={getMessageFromRtkError(gridStatusError)}
              tryAgain={{
                onTryAgain: async () => {
                  await refetchGridStatus();
                }
              }}
            />
          )}
          {systemStatus && (
            <>
              {systemStatus.kind === "connected" && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Mode:
                  </Typography>
                  <Chip label={systemStatus.mode} size="small" />
                </Box>
              )}
              {systemStatus.kind === "connected" && systemStatus.stopCode && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Stop Code:
                  </Typography>
                  <Typography variant="body2">
                    {systemStatus.stopCode}
                  </Typography>
                </Box>
              )}
              {systemStatus.kind === "error" && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Grid Error:
                  </Typography>
                  <Typography variant="body2" color="error">
                    {systemStatus.message}
                  </Typography>
                </Box>
              )}
            </>
          )}
          {(logPubError || autostoreTasksError) &&
            !logPubLoading &&
            !autostoreTasksLoading && (
              <ErrorPanel
                message={getMessageFromRtkError(
                  logPubError ?? autostoreTasksError
                )}
                tryAgain={{
                  onTryAgain: refetchAll
                }}
              />
            )}
          {!logPubError &&
            !autostoreTasksError &&
            logPubData &&
            autostoreTasks && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Total Tasks:
                    </Typography>
                    <Typography variant="body2">{totalTasks}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Picking Tasks:
                    </Typography>
                    <Typography variant="body2">{totalPickingTasks}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Picking Task Groups:
                    </Typography>
                    <Typography variant="body2">
                      {pickingTaskGroupCount.length}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Prepared Bins:
                    </Typography>
                    <Typography variant="body2">
                      {preparedBins.length}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Prepared Tasks:
                    </Typography>
                    <Typography variant="body2">
                      {preparedTasks.length} (
                      {Math.trunc((preparedTasks.length / totalTasks) * 100)}%)
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
        </Box>
      </Card>
    </>
  );
};

export const SystemStatus = () => {
  const { data: autostoreGrids, error } = useGetAutostoreGridsQuery();
  const fetchStatusEndpointsInterval = useAppSelector(
    selectFetchStatusEndpointsInterval
  );

  // Get timestamp from device health (since all endpoints refresh together)
  const { fulfilledTimeStamp: lastRefreshTimestamp } = useGetDevicesHealthQuery(
    undefined,
    {
      pollingInterval:
        fetchStatusEndpointsInterval > 0 ? fetchStatusEndpointsInterval : 5000
    }
  );

  const { errorToast } = useToast();

  useEffect(() => {
    if (error) errorToast(getMessageFromRtkError(error));
  }, [error, errorToast]);

  return (
    <Box>
      {/* Last Refreshed timestamp in top-right */}
      {lastRefreshTimestamp && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            Last Refreshed: {formatDateTime(new Date(lastRefreshTimestamp))}
          </Typography>
        </Box>
      )}

      {/* Status Cards */}
      <Stack flexDirection="row" flexWrap="wrap" gap={2}>
        {(autostoreGrids ?? []).map((g) => (
          <GridInfo
            key={g.autostoreGridId}
            autostoreGridId={g.autostoreGridId}
          />
        ))}
        <PTLHealthCard />
      </Stack>
    </Box>
  );
};
